import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { TColor } from 'app/services/api5-service/api.interface';

import { CheckboxLabelPosition } from '@ispui/checkbox/dist/types/components/checkbox/checkbox';

import { OnOff } from './model/checkbox.interface';

@Component({
  selector: 'isp-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./scss/checkbox.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  value: OnOff = 'off';

  @Input() minus = false;

  @Input() labelPosition: CheckboxLabelPosition = 'right';

  @Input() name: string;

  @Input() required: boolean;

  @Input() captionOn: string;

  @Input() captionOff: string;

  @Input() colorOn?: TColor;

  @Input() colorOff?: TColor;

  @Input() disabled?: boolean;

  @Input() toggle?: boolean;

  get caption(): string {
    const caption = this.value === 'on' ? this.captionOn : this.captionOff;

    const asterisk = `${
      this.required ? '<span class="isp-required-mark">*</span>' : ''
    }`;

    return `${caption}${asterisk}`;
  }

  get color(): string | undefined {
    return this.value === 'on' ? this.colorOn : this.colorOff;
  }

  constructor(private readonly cdr: ChangeDetectorRef) {}

  private onChange: (value: OnOff | '') => void = () => undefined;

  private onTouch: () => void = () => undefined;

  onValueChange(event: CustomEvent<{ checked: OnOff }>): void {
    const newValue = event.detail.checked;
    if (this.value === newValue) {
      return;
    }

    this.value = newValue;

    this.onChange(newValue);
    this.onTouch();
  }

  writeValue(value: OnOff | null): void {
    this.value = value || 'off';
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: OnOff | '') => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
